import styled from '@emotion/styled';
import React, { FC } from 'react';
import { C, sweep } from './styles';

const Arc = styled.path<{ delay: number }>`
  animation: ${sweep} 1.6s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.6;
  }
`;

/** The contactless mark, the same symbol printed on the card itself. */
export const ContactlessIcon: FC<{ size?: number; animated?: boolean }> = ({
  size = 26,
  animated = false,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {[
      { d: 'M7.5 8.5a6 6 0 010 7', delay: 0 },
      { d: 'M11 6a9.5 9.5 0 010 12', delay: 180 },
      { d: 'M14.5 3.5a13 13 0 010 17', delay: 360 },
    ].map((arc) =>
      animated ? (
        <Arc
          key={arc.d}
          d={arc.d}
          delay={arc.delay}
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      ) : (
        <path
          key={arc.d}
          d={arc.d}
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      ),
    )}
  </svg>
);

export const SearchIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2.5 7.5l3 3 6-6.5"
      stroke={C.textOnLight}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PersonPlusIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="10" cy="8" r="3.6" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M3.6 20c0-3.3 2.9-5.6 6.4-5.6 1.2 0 2.3.3 3.2.7"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path d="M18 14v6M15 17h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/** Scheme marks, drawn rather than imported so they inherit our palette. */
export const BrandMark: FC<{ brand?: string }> = ({ brand }) => {
  const normalised = (brand ?? '').toLowerCase();

  if (normalised.includes('mc') || normalised.includes('master')) {
    return (
      <svg width="26" height="16" viewBox="0 0 26 16" aria-hidden="true">
        <circle cx="10" cy="8" r="6.2" fill={C.textSecondary} opacity="0.9" />
        <circle cx="16" cy="8" r="6.2" fill={C.textMuted} opacity="0.9" />
      </svg>
    );
  }

  if (normalised.includes('visa')) {
    return (
      <svg width="26" height="16" viewBox="0 0 26 16" aria-hidden="true">
        <path d="M3 11.5L6 4.5h2.2l-3 7H3z" fill={C.textSecondary} />
        <path d="M10 11.5l1.6-7h2.1l-1.6 7H10z" fill={C.textSecondary} />
        <path d="M16.5 4.5h2L23 11.5h-2.2l-.6-1.2h-2.6l-.4 1.2H15l1.5-7z" fill={C.textMuted} />
      </svg>
    );
  }

  return (
    <svg width="26" height="16" viewBox="0 0 26 16" aria-hidden="true">
      <rect
        x="1"
        y="1.5"
        width="24"
        height="13"
        rx="2.5"
        fill="none"
        stroke={C.textMuted}
        strokeWidth="1.4"
      />
      <path d="M1 6h24" stroke={C.textMuted} strokeWidth="1.4" />
    </svg>
  );
};
